import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import styled, { css } from 'styled-components';
import {
  Anchor,
  DetailedIconHandshake,
  DetailedIconPiggybank,
  Grid,
  Heading,
  mediaMixin,
  spaceMixin,
  Text,
  Upsell,
} from '@zillow/constellation';
import useQueryVariables from '../../../hooks/useQueryVariables';
import { getIsAcceptedOrInvitedToMoveIn, INVITE_TO_MOVE_IN_QUERY } from '../../../utils/inviteToMoveIn';
import { getLeasesPath, getPaymentsPath } from '../../../constants/urls';
import { useSelector } from 'react-redux';
import { listingAliasSelector } from '../../../selectors';
import { useListingUser } from '../../../hooks/useListingUser';
import {
  emitOpenLeasesConfirmationModalEvent,
  emitOpenLeasesSummaryPageEvent,
  emitOpenPaymentsConfirmationModalEvent,
  emitOpenPaymentsSummaryPageEvent,
} from '../../../../client/analytics/clickstreamEvents';

const StyledParentGrid = styled(Grid)`
  width: 100%;

  /* Only have margin bottom when on page, not in modal. */
  margin-bottom: ${(props) => (props.isInModal ? 0 : spaceMixin('lg'))};

  ${Upsell} {
    grid-column-end: span 12;
    :not(:last-child) {
      padding-bottom: ${spaceMixin('md')};
      margin-bottom: ${spaceMixin('md')};
    }
  }

  ${(props) =>
    !props.isInModal &&
    css`
      /* >= 769px */
      ${mediaMixin(
        'lg',
        css`
            ${Upsell} {
                grid-column-end: span 6;
                :not(:last-child) {
                    margin-bottom: 0;
                    margin-right: ${spaceMixin('lg')};
                    border-bottom: 0;
                }
            }
          }
        `,
      )}
    `}
`;

function LeasesPaymentsUpsell({ isInModal }) {
  const listingAlias = useSelector(listingAliasSelector);
  const { applicationId, propertyId } = useQueryVariables();
  const { data, loading, error } = useQuery(INVITE_TO_MOVE_IN_QUERY, { variables: { propertyId, applicationId } });
  const { user, listing } = useListingUser();
  const isAcceptedOrInvitedToMoveIn = getIsAcceptedOrInvitedToMoveIn(data);

  function onLeasesSectionClick() {
    if (isInModal) {
      emitOpenLeasesConfirmationModalEvent(listing, user);
    } else {
      emitOpenLeasesSummaryPageEvent(listing, user);
    }
  }

  function onPaymentsSectionClick() {
    if (isInModal) {
      emitOpenPaymentsConfirmationModalEvent(listing, user);
    } else {
      emitOpenPaymentsSummaryPageEvent(listing, user);
    }
  }

  if (loading || error) {
    return null;
  }

  const shouldRenderITMIUpsell = Boolean(isAcceptedOrInvitedToMoveIn || isInModal);

  return (
    shouldRenderITMIUpsell && (
      <StyledParentGrid
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        justifyItems="center"
        isInModal={isInModal}
        className="zon_ApplicationSummary__LeasesAndPaymentsUpsell"
      >
        {isInModal && (
          <Grid gridColumnEnd="span 12">
            <Heading level={4} marginBottom="sm">
              Next steps
            </Heading>
          </Grid>
        )}
        <Upsell
          heading={<Heading level={6}>Create or upload a lease</Heading>}
          body={
            <Text>
              We guide you through each step, so you can quickly customize your lease with your property in mind.
            </Text>
          }
          media={<DetailedIconHandshake size="lg" />}
          actionButton={
            <Anchor data-test="leases-payments-upsell-cta" href={getLeasesPath(listingAlias, applicationId)}>
              Add lease
            </Anchor>
          }
          onClick={onLeasesSectionClick}
          closeable={false}
          background="backgroundBlue"
          data-test="leases-upsell"
        />
        <Upsell
          heading={<Heading level={6}>Collect payments</Heading>}
          body={
            <Text>
              The free, secure way to get your deposit or collect rent right into your bank account. Your tenant gets a
              free way to pay and more flexibility.
            </Text>
          }
          media={<DetailedIconPiggybank size="lg" />}
          actionButton={
            <Anchor data-test="leases-payments-upsell-cta" href={getPaymentsPath(listingAlias)}>
              Start collecting rent
            </Anchor>
          }
          onClick={onPaymentsSectionClick}
          closeable={false}
          background="backgroundBlue"
          data-test="payments-upsell"
        />
      </StyledParentGrid>
    )
  );
}

LeasesPaymentsUpsell.propTypes = {
  isInModal: PropTypes.bool,
};

export default LeasesPaymentsUpsell;
